import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Table from "../../../components/Table";
import TableSkeleton from "../../../components/TableSkeleton";
import Nodata from "../../../components/Nodata";
import { handleSpringDataRequest } from "../../../utils/apiUtils";
import moment from "moment";

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentMonthDateRange = () => {
  const now = new Date();
  return {
    startDate: getLocalDateString(new Date(now.getFullYear(), now.getMonth(), 1)),
    endDate: getLocalDateString(now),
  };
};

const getLastMonthDateRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    startDate: getLocalDateString(start),
    endDate: getLocalDateString(end),
  };
};

const getLastSixMonthsDateRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  return {
    startDate: getLocalDateString(start),
    endDate: getLocalDateString(now),
  };
};

const getThisYearDateRange = () => {
  const now = new Date();
  return {
    startDate: getLocalDateString(new Date(now.getFullYear(), 0, 1)),
    endDate: getLocalDateString(now),
  };
};

const formatLogTimestamp = (value) => {
  if (!value && value !== 0) {
    return "-";
  }

  if (typeof value === "object" && value?.time) {
    return moment(value.time).format("MMM DD, YYYY hh:mm A");
  }

  if (typeof value === "number") {
    return moment(value).format("MMM DD, YYYY hh:mm A");
  }

  const parsedMoment = moment(value);
  if (parsedMoment.isValid()) {
    return parsedMoment.format("MMM DD, YYYY hh:mm A");
  }

  return String(value);
};

const parseLogsResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (typeof response === "string") {
    try {
      const parsed = JSON.parse(response);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  return [];
};

export default function AccountLogsManagement({ groupId }) {
  const defaultCurrentMonthRange = useMemo(getCurrentMonthDateRange, []);
  const [startDate, setStartDate] = useState(defaultCurrentMonthRange.startDate);
  const [endDate, setEndDate] = useState(defaultCurrentMonthRange.endDate);
  const [quickRange, setQuickRange] = useState("current_month");
  const [isLoading, setIsLoading] = useState(false);
  const [rowData, setRowData] = useState([]);

  const columnDefs = useMemo(
    () => [
      { field: "id", headerName: "ID", minWidth: 90, maxWidth: 120 },
      {
        field: "log_timestamp",
        headerName: "Log timestamp",
        minWidth: 210,
      },
      {
        field: "user_email",
        headerName: "User email",
        minWidth: 220,
      },
      {
        field: "user_ip",
        headerName: "User IP",
        minWidth: 160,
      },
      {
        field: "log_component",
        headerName: "Component",
        minWidth: 150,
      },
      {
        field: "log_sub_component",
        headerName: "Sub component",
        minWidth: 190,
      },
      {
        field: "log_text",
        headerName: "Action",
        minWidth: 400,
        flex: 1,
        wrapText: true,
        autoHeight: true,
      },
    ],
    []
  );

  const fetchAccountLogs = () => {
    if (!groupId) {
      return;
    }
    setIsLoading(true);
    handleSpringDataRequest(
      `core/api/v1/account/get-account-logs?groupId=${groupId}&startDate=${startDate}&endDate=${endDate}`
    )
      .then((response) => {
        const parsedLogs = parseLogsResponse(response);
        const normalizedRows = parsedLogs.map((log) => ({
          id: log?.id,
          group_id: log?.group_id ?? log?.groupId,
          user_email: log?.user_email ?? log?.userEmail ?? "-",
          user_ip: log?.user_ip ?? log?.userIp ?? "-",
          log_component: log?.log_component ?? log?.logComponent ?? "-",
          log_sub_component:
            log?.log_sub_component ?? log?.logSubComponent ?? "-",
          log_text: log?.log_text ?? log?.logText ?? "-",
          log_timestamp: formatLogTimestamp(
            log?.log_timestamp ?? log?.logTimestamp
          ),
        }));
        setRowData(normalizedRows);
      })
      .catch(() => {
        setRowData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchAccountLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const applyQuickRange = (range) => {
    let dateRange = null;

    if (range === "last_month") {
      dateRange = getLastMonthDateRange();
    } else if (range === "last_6_months") {
      dateRange = getLastSixMonthsDateRange();
    } else if (range === "this_year") {
      dateRange = getThisYearDateRange();
    } else if (range === "current_month") {
      dateRange = getCurrentMonthDateRange();
    }

    if (dateRange) {
      setStartDate(dateRange.startDate);
      setEndDate(dateRange.endDate);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ mb: 2, alignItems: { md: "flex-end" } }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="account-logs-quick-range-label">Quick range</InputLabel>
          <Select
            labelId="account-logs-quick-range-label"
            value={quickRange}
            label="Quick range"
            onChange={(e) => {
              const value = e.target.value;
              setQuickRange(value);
              applyQuickRange(value);
            }}
          >
            <MenuItem value="">Custom range</MenuItem>
            <MenuItem value="current_month">Current month</MenuItem>
            <MenuItem value="last_month">Last month</MenuItem>
            <MenuItem value="last_6_months">Last 6 months</MenuItem>
            <MenuItem value="this_year">This year</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Start date"
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setQuickRange("");
          }}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="End date"
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setQuickRange("");
          }}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button variant="custom-button" onClick={fetchAccountLogs}>
          Show Logs
        </Button>
      </Stack>

      {isLoading ? (
        <TableSkeleton />
      ) : rowData?.length > 0 ? (
        <Table rowData={rowData} columnDefs={columnDefs} />
      ) : (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ mb: 1, color: "#666" }}>
            No account logs found for selected date range.
          </Typography>
          <Nodata />
        </Box>
      )}
    </Box>
  );
}
